import {
  and,
  ControlProps,
  hasType,
  isDescriptionHidden,
  OwnPropsOfEnum,
  RankedTester,
  rankWith,
  schemaMatches,
  schemaSubPathMatches,
  uiTypeIs,
} from "@jsonforms/core";
import { withJsonFormsMultiEnumProps } from "@jsonforms/react";
import { useMemo } from "react";

import Select from "@/components/elements/Select";
import InputErrorTooltip from "@/components/elements/InputErrorTooltip";
import { InputDescription } from "@/components/primitives/InputDescription";
import FieldLabel from "@/components/primitives/InputLabel";

type MultiEnumControlProps = ControlProps & OwnPropsOfEnum;

export const multiEnumControlTester: RankedTester = rankWith(
  5,
  and(
    uiTypeIs("Control"),
    schemaMatches(
      (schema) => hasType(schema, "array") && !Array.isArray(schema.items),
    ),
    schemaSubPathMatches(
      "items",
      (schema) => Array.isArray(schema.oneOf) || Array.isArray(schema.enum),
    ),
  ),
);

function MultiEnumControlComponent({
  data,
  handleChange,
  path,
  label,
  description,
  errors,
  options = [],
  visible,
  enabled,
  required,
  config,
  schema,
}: MultiEnumControlProps) {
  const showDescription =
    !isDescriptionHidden(
      visible,
      description,
      false,
      config?.showUnfocusedDescription,
    ) && !!description;
  const hasError = errors !== undefined && errors.length > 0;

  const selectedValues = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data
      .map((value) => options.find((opt) => opt.value === value))
      .filter((opt) => opt != null);
  }, [data, options]);

  const { maxItems, minItems } = schema;
  const atMaxItems = maxItems != null && selectedValues.length >= maxItems;

  const handleSelectChange = (opts: any) => {
    const next = (opts ?? []).map((opt: any) => opt.value);
    const isRemoval = next.length < selectedValues.length;
    if (isRemoval && minItems != null && next.length < minItems) return;
    handleChange(path, maxItems != null ? next.slice(0, maxItems) : next);
  };

  if (!visible) return null;

  return (
    <div>
      <FieldLabel htmlFor={path} required={required}>
        {label}
      </FieldLabel>

      <div className="flex items-center">
        <div className="grow">
          <Select
            isMulti
            inputId={path}
            options={options}
            value={selectedValues}
            onChange={handleSelectChange}
            isOptionDisabled={(option: any) =>
              option.disabled ||
              (atMaxItems &&
                !selectedValues.some((opt) => opt.value === option.value))
            }
            isClearable={minItems == null || minItems === 0}
            disabled={!enabled}
          />
        </div>

        {hasError && (
          <InputErrorTooltip
            iconClassNames="w-6"
            error={label + " " + errors}
          />
        )}
      </div>

      {showDescription && (
        <InputDescription className="m-1">{description}</InputDescription>
      )}
    </div>
  );
}

export const MultiEnumControl = withJsonFormsMultiEnumProps(
  MultiEnumControlComponent,
);
